import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, MapPin, ExternalLink, Video, Ticket, Users, Ban, RefreshCw, Globe } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { fileUrl } from '../../lib/api';
import { AddToCalendar } from './AddToCalendar';
import { ShareEvent } from './ShareEvent';

export const RACE_STYLES = {
  mayor: 'bg-[#1a2744] text-white border-0',
  council: 'bg-[#EAF2FF] text-[#1a2744] border-[#c7d7f5]',
  trustee: 'bg-[#FBF7EE] text-[#7a5c1c] border-[#e5d3a3]',
  general: 'bg-slate-100 text-slate-700 border-slate-200',
};

export const RACE_SHORT = { mayor: 'Mayor', council: 'Councillor', trustee: 'School Trustee', general: 'General' };

const mapsHref = (ev) => {
  const q = [ev.venue_name, ev.address].filter(Boolean).join(', ');
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q + ', Kawartha Lakes, ON')}` : '';
};

// One election event. Used on the Election Centre landing spotlight, the
// full event list and the admin preview. Never shows submitter details
// (the public API does not send them).
export const EventCard = ({ ev, compact = false, testId = 'election-event-card' }) => {
  if (!ev) return null;
  const past = !ev.upcoming;
  const cancelled = ev.cancelled;
  return (
    <Card className={`overflow-hidden ${cancelled ? 'border-red-200 bg-red-50/40' : past ? 'bg-slate-50/60' : 'card-lift'}`} data-testid={testId} data-event-id={ev.id}>
      <CardContent className={compact ? 'p-4' : 'p-5'}>
        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="event-badges">
          {cancelled && <Badge className="bg-red-100 text-red-800 border-red-200"><Ban className="h-3 w-3 mr-1" /> Cancelled</Badge>}
          {!cancelled && ev.reschedule_note && <Badge className="bg-amber-100 text-amber-800 border-amber-200"><RefreshCw className="h-3 w-3 mr-1" /> Rescheduled</Badge>}
          {!cancelled && past && <Badge variant="outline" className="border-slate-300 text-slate-600">Past event</Badge>}
          {!cancelled && !past && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Upcoming</Badge>}
          {(ev.races || []).map((r) => <Badge key={r} className={RACE_STYLES[r] || RACE_STYLES.general}>{RACE_SHORT[r] || r}</Badge>)}
          <Badge variant="outline" className="border-slate-300 text-slate-700">{ev.ward_label}</Badge>
          <Badge variant="outline" className="border-slate-300 text-slate-600">{ev.event_type_label}</Badge>
        </div>
        <h3 className={`font-serif font-bold text-[#1a2744] leading-snug ${compact ? 'text-base' : 'text-lg'} ${cancelled ? 'line-through decoration-red-400' : ''}`} data-testid="event-title">
          {ev.title}
        </h3>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-700">
          <p className="flex items-start gap-2" data-testid="event-date"><CalendarDays className="h-4 w-4 mt-0.5 text-[#24406f] shrink-0" /><span><strong className="text-[#1a2744]">{ev.date_label}</strong></span></p>
          <p className="flex items-start gap-2" data-testid="event-time"><Clock className="h-4 w-4 mt-0.5 text-[#24406f] shrink-0" /><span>{ev.time_label}{ev.start_time ? ' ET' : ''}</span></p>
          {(ev.venue_name || ev.address) && (
            <p className="flex items-start gap-2 sm:col-span-2" data-testid="event-venue"><MapPin className="h-4 w-4 mt-0.5 text-[#24406f] shrink-0" />
              <span>{ev.venue_name}{ev.venue_name && ev.address ? ', ' : ''}{ev.address}
                {mapsHref(ev) && <> <a href={mapsHref(ev)} target="_blank" rel="noreferrer" className="underline text-[#24406f] ml-1" data-testid="event-map-link">Map</a></>}
              </span>
            </p>
          )}
          {ev.online_url && <p className="flex items-start gap-2 sm:col-span-2"><Globe className="h-4 w-4 mt-0.5 text-[#24406f] shrink-0" /><a href={ev.online_url} target="_blank" rel="noreferrer" className="underline text-[#24406f] break-all" data-testid="event-online-link">Online event link</a></p>}
          {ev.organizer && <p className="flex items-start gap-2 sm:col-span-2"><Users className="h-4 w-4 mt-0.5 text-[#24406f] shrink-0" /><span>Organizer: {ev.organizer}</span></p>}
        </div>
        {(ev.candidates || []).length > 0 && (
          <p className="mt-2 text-sm text-slate-700" data-testid="event-candidates"><strong className="text-[#1a2744]">Candidates{ev.candidates_confirmed ? '' : ' (as announced by the organizer)'}:</strong> {ev.candidates.join(', ')}</p>
        )}
        {cancelled && ev.cancellation_note && <p className="mt-2 text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg p-2.5" data-testid="event-cancellation-note">{ev.cancellation_note}</p>}
        {!cancelled && ev.reschedule_note && <p className="mt-2 text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg p-2.5" data-testid="event-reschedule-note">{ev.reschedule_note}</p>}
        {!compact && ev.description && <p className="mt-3 text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-testid="event-description">{ev.description}</p>}
        {!compact && ev.image_url && <img src={fileUrl(ev.image_url)} alt={`${ev.title} poster`} className="mt-3 rounded-lg border border-slate-200 max-h-72 object-contain bg-white" loading="lazy" data-testid="event-image" />}
        {!compact && ev.video_embed_url && (
          <div className="mt-3 aspect-video rounded-lg overflow-hidden border border-slate-200 bg-black" data-testid="event-video">
            <iframe src={ev.video_embed_url} title={`${ev.title} video`} className="w-full h-full" loading="lazy" allowFullScreen allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" />
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {!past && <AddToCalendar ev={ev} />}
          {!past && <ShareEvent ev={ev} />}
          {ev.registration_url && !cancelled && (
            <Button asChild size="sm" className="h-9 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid="event-register-btn">
              <a href={ev.registration_url} target="_blank" rel="noreferrer"><Ticket className="h-3.5 w-3.5 mr-1.5" /> Register</a>
            </Button>
          )}
          {ev.video_url && !ev.video_embed_url && (
            <Button asChild size="sm" variant="outline" className="h-9 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="event-video-link">
              <a href={ev.video_url} target="_blank" rel="noreferrer"><Video className="h-3.5 w-3.5 mr-1.5" /> Watch the video</a>
            </Button>
          )}
          {ev.source_url && (
            <a href={ev.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center h-9 text-sm text-slate-600 underline underline-offset-2" data-testid="event-source-link">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Source{ev.source_name ? `: ${ev.source_name}` : ''}
            </a>
          )}
          {compact && <Link to={`/elections/events#event-${ev.id}`} className="inline-flex items-center h-9 text-sm font-bold text-[#1a2744] underline underline-offset-2" data-testid="event-details-link">Details</Link>}
        </div>
      </CardContent>
    </Card>
  );
};
